import { useState, useCallback } from 'react';
import { agents as defaultAgents } from '../data/agents';
import type { Agent } from '../data/agents';

export interface UserAgent extends Agent {
  isCustom: true;
}

export function useAgents() {
  const [customPrompts, setCustomPrompts] = useState<Record<string, string>>(() => {
    try {
      const saved = localStorage.getItem('customAgentPrompts');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const [userAgents, setUserAgents] = useState<UserAgent[]>(() => {
    try {
      const saved = localStorage.getItem('userAgents');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  const agents: (Agent & { isCustom?: boolean })[] = [
    ...defaultAgents,
    ...userAgents,
  ];

  const getAgentPrompt = useCallback((agent: Agent) => {
    return customPrompts[agent.name] || agent.prompt;
  }, [customPrompts]);

  const updatePrompt = useCallback((agentName: string, prompt: string) => {
    setCustomPrompts(prev => {
      const next = { ...prev, [agentName]: prompt };
      localStorage.setItem('customAgentPrompts', JSON.stringify(next));
      return next;
    });
  }, []);

  const resetPrompt = useCallback((agentName: string) => {
    setCustomPrompts(prev => {
      const next = { ...prev };
      delete next[agentName];
      localStorage.setItem('customAgentPrompts', JSON.stringify(next));
      return next;
    });
  }, []);

  const addAgent = useCallback((agent: { name: string; description: string; prompt: string }) => {
    setUserAgents(prev => {
      const next = [...prev, { ...agent, isCustom: true as const }];
      localStorage.setItem('userAgents', JSON.stringify(next));
      return next;
    });
  }, []);

  const deleteAgent = useCallback((agentName: string) => {
    setUserAgents(prev => {
      const next = prev.filter(a => a.name !== agentName);
      localStorage.setItem('userAgents', JSON.stringify(next));
      return next;
    });
    // Also remove any custom prompt override
    setCustomPrompts(prev => {
      const next = { ...prev };
      delete next[agentName];
      localStorage.setItem('customAgentPrompts', JSON.stringify(next));
      return next;
    });
  }, []);

  return {
    agents,
    defaultAgents,
    customPrompts,
    userAgents,
    getAgentPrompt,
    updatePrompt,
    resetPrompt,
    addAgent,
    deleteAgent,
  };
}
